import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Fanfiction: your shelf of fics. Add one from its AO3 download (EPUB or PDF)
// and the details fill themselves in — fandom, pairing, characters, tags,
// words, chapters — and the file goes into your vault. Each fic opens into its
// journal: your notes, your Kindle highlights, when you read it.

const STATUS = [
  { key: 'reading', label: 'reading' },
  { key: 'want', label: 'want to read' },
  { key: 'finished', label: 'finished' },
  { key: 'dropped', label: 'dropped' },
];
const STATUS_LABEL = Object.fromEntries(STATUS.map(x => [x.key, x.label]));

const shortDate = (d) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }) : '');
const words = (n) => (n == null ? '' : n >= 1e6 ? `${(n / 1e6).toFixed(1)}M words` : n >= 1000 ? `${Math.round(n / 100) / 10}k words` : `${n} words`);
const chaptersLabel = (f) => (f.chapters_posted ? `${f.chapters_posted}/${f.chapters_total ?? '?'}` : '');

const Hearts = ({ value = 0, size = 12 }) => (
  <span style={{ display: 'inline-flex', gap: '2px' }} aria-label={`${value} of 5`}>
    {[1, 2, 3, 4, 5].map(i => (
      <svg key={i} width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 20.5s-7.5-4.6-9.2-9.3C1.6 7.8 3.8 4.5 7.2 4.5c2 0 3.5 1.1 4.8 2.8 1.3-1.7 2.8-2.8 4.8-2.8 3.4 0 5.6 3.3 4.4 6.7-1.7 4.7-9.2 9.3-9.2 9.3z"
          fill={i <= value ? '#c9935a' : 'none'} stroke="#c9935a" strokeWidth="1.6" opacity={i <= value ? 1 : 0.35} />
      </svg>
    ))}
  </span>
);

const UploadIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 16V4" /><path d="M7 9l5-5 5 5" /><path d="M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3" />
  </svg>
);

export default function Fanfiction() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const [fics, setFics] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [vaultOn, setVaultOn] = useState(true);
  const [filter, setFilter] = useState(() => { try { return localStorage.getItem('ficFilter') || 'reading'; } catch (e) { return 'reading'; } });
  const [q, setQ] = useState('');
  const [fandom, setFandom] = useState('');
  const [adding, setAdding] = useState(false);
  const [addStatus, setAddStatus] = useState('reading');
  const [uploading, setUploading] = useState(false);
  const [byHand, setByHand] = useState(false);
  const [hand, setHand] = useState({ title: '', author: '', url: '', fandom: '', pairing: '', words: '', chapters: '' });
  const [notice, setNotice] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const fileRef = useRef(null);

  useEffect(() => {
    if (!token) { navigate('/'); return; }
    load();
  }, []);

  const describeError = (err) => {
    const d = err?.response?.data?.detail;
    return typeof d === 'string' ? d : (err?.message || 'something went wrong');
  };

  const load = () => {
    axios.get(`${API}/fanfics?token=${token}`)
      .then(res => { setFics(res.data.fanfics || []); setVaultOn(res.data.vault_connected !== false); })
      .catch(err => setErrorMsg(`couldn't load your fics: ${describeError(err)}`))
      .finally(() => setLoaded(true));
  };

  const pickFilter = (k) => {
    setFilter(k);
    try { localStorage.setItem('ficFilter', k); } catch (e) { /* fine */ }
  };

  const onPickFile = (e) => {
    const file = (e.target.files || [])[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    setErrorMsg('');
    setNotice('');
    const fd = new FormData();
    fd.append('file', file);
    axios.post(`${API}/fanfics/import?token=${token}&status=${addStatus}`, fd)
      .then(res => {
        const f = res.data.fanfic;
        const bits = [];
        if (!res.data.read_from_file) bits.push("this didn't look like an AO3 download, so only the title was filled in — add the rest on its page");
        if (res.data.file_note) bits.push(res.data.file_note);
        setNotice(bits.join(' · '));
        setAdding(false);
        if (!bits.length) navigate(`/fanfiction/${f.id}`);
        else { load(); pickFilter(addStatus); }
      })
      .catch(err => setErrorMsg(`couldn't add it: ${describeError(err)}`))
      .finally(() => setUploading(false));
  };

  const addByHand = () => {
    if (!hand.title.trim()) return;
    const list = (v) => v.split(',').map(x => x.trim()).filter(Boolean);
    axios.post(`${API}/fanfics`, {
      token, title: hand.title, author: hand.author || null, url: hand.url || null,
      fandoms: list(hand.fandom), relationships: list(hand.pairing),
      words: hand.words ? parseInt(hand.words.replace(/\D/g, ''), 10) || null : null,
      chapters_total: hand.chapters ? parseInt(hand.chapters, 10) || null : null, status: addStatus,
    })
      .then(res => navigate(`/fanfiction/${res.data.fanfic.id}`))
      .catch(err => setErrorMsg(`couldn't add it: ${describeError(err)}`));
  };

  const fandoms = [...new Set(fics.flatMap(f => f.fandoms || []))].sort((a, b) => a.localeCompare(b));
  const needle = q.trim().toLowerCase();
  const shown = fics.filter(f => {
    if (filter !== 'all' && f.status !== filter) return false;
    if (fandom && !(f.fandoms || []).includes(fandom)) return false;
    if (!needle) return true;
    return [f.title, f.author, ...(f.fandoms || []), ...(f.relationships || []), ...(f.characters || []), ...(f.tags || [])]
      .some(x => (x || '').toLowerCase().includes(needle));
  });
  const count = (k) => fics.filter(f => f.status === k).length;
  const wordsRead = fics.reduce((a, f) => a + (f.words || 0) * (f.times_read || 0), 0);

  return (
    <div style={s.page}>
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=DM+Sans:wght@300;400;500&display=swap" rel="stylesheet" />
      <header style={s.header}>
        <button style={s.back} onClick={() => navigate('/companions')} aria-label="back">←</button>
        <div style={s.title}>fanfiction</div>
        <button style={adding ? s.addBtnOn : s.addBtn} onClick={() => { setAdding(a => !a); setByHand(false); }}>
          {adding ? 'close' : '+ add a fic'}
        </button>
      </header>

      <main style={s.main}>
        {errorMsg && <div style={s.error} onClick={() => setErrorMsg('')}>⚠ {errorMsg}</div>}
        {notice && <div style={s.notice} onClick={() => setNotice('')}>{notice}</div>}

        {adding && (
          <section style={s.addCard}>
            <div style={s.addTitle}>add a fic</div>
            <div style={s.hint}>
              upload the EPUB or PDF from AO3 (the download button on the fic) and everything fills itself in.
              {vaultOn ? ' the file is kept in your vault.' : ' your vault isn’t connected yet, so only the details will be saved for now.'}
            </div>
            <div style={s.row}>
              <span style={s.smallLabel}>it's</span>
              {STATUS.map(x => (
                <button key={x.key} style={s.chip(addStatus === x.key)} onClick={() => setAddStatus(x.key)}>{x.label}</button>
              ))}
            </div>
            <input ref={fileRef} type="file" accept=".epub,.pdf,application/epub+zip,application/pdf" style={{ display: 'none' }} onChange={onPickFile} />
            <div style={s.row}>
              <button style={s.primary} onClick={() => fileRef.current?.click()} disabled={uploading}>
                <UploadIcon /> {uploading ? 'reading it…' : 'upload EPUB or PDF'}
              </button>
              <button style={s.quiet} onClick={() => setByHand(v => !v)}>{byHand ? 'hide' : 'or add it by hand'}</button>
            </div>
            {byHand && (
              <div style={s.handGrid}>
                {[['title', 'title'], ['author', 'author'], ['url', 'link (AO3)'], ['fandom', 'fandom'], ['pairing', 'pairing (A/B)'], ['words', 'words'], ['chapters', 'chapters']].map(([k, label]) => (
                  <label key={k} style={s.field}>
                    <span style={s.smallLabel}>{label}</span>
                    <input style={s.input} value={hand[k]} inputMode={k === 'words' || k === 'chapters' ? 'numeric' : undefined}
                      onChange={e => setHand(h => ({ ...h, [k]: e.target.value }))} />
                  </label>
                ))}
                <button style={hand.title.trim() ? s.primary : s.primaryOff} disabled={!hand.title.trim()} onClick={addByHand}>add</button>
              </div>
            )}
          </section>
        )}

        {fics.length > 0 && (
          <div style={s.summary}>
            {count('reading')} reading · {count('finished')} finished{wordsRead ? ` · ${words(wordsRead).replace(' words', '')} words read` : ''}
          </div>
        )}

        <div style={s.filters}>
          {[...STATUS, { key: 'all', label: 'all' }].map(x => (
            <button key={x.key} style={s.filterBtn(filter === x.key)} aria-pressed={filter === x.key} onClick={() => pickFilter(x.key)}>
              {x.label}{x.key !== 'all' && count(x.key) ? ` ${count(x.key)}` : ''}
            </button>
          ))}
        </div>
        {fics.length > 0 && (
          <div style={s.row}>
            <input style={{ ...s.input, flex: 1, minWidth: '150px' }} value={q} onChange={e => setQ(e.target.value)} placeholder="search title, pairing, character, tag…" aria-label="search" />
            {fandoms.length > 1 && (
              <select style={s.select} value={fandom} onChange={e => setFandom(e.target.value)} aria-label="fandom">
                <option value="">every fandom</option>
                {fandoms.map(x => <option key={x} value={x}>{x}</option>)}
              </select>
            )}
          </div>
        )}

        <div style={s.list}>
          {shown.map(f => (
            <button key={f.id} style={s.card} onClick={() => navigate(`/fanfiction/${f.id}`)}>
              <div style={s.cardTop}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={s.ficTitle}>{f.title}</div>
                  {f.author && <div style={s.by}>by {f.author}</div>}
                </div>
                <span style={s.status(f.status)}>{STATUS_LABEL[f.status] || f.status}</span>
              </div>
              {(f.relationships || []).length > 0 && <div style={s.pairing}>{f.relationships.slice(0, 2).join(' · ')}{f.relationships.length > 2 ? ` +${f.relationships.length - 2}` : ''}</div>}
              {(f.fandoms || []).length > 0 && <div style={s.fandom}>{f.fandoms.join(', ')}</div>}
              <div style={s.meta}>
                {[words(f.words), chaptersLabel(f) && `${chaptersLabel(f)} ch`, f.complete === false ? 'WIP' : f.complete ? 'complete' : '', f.rating].filter(Boolean).join(' · ')}
              </div>
              <div style={s.metaRow}>
                <span>
                  {f.status === 'finished' && f.finished_on ? `finished ${shortDate(f.finished_on)}`
                    : f.started_on ? `started ${shortDate(f.started_on)}` : `added ${shortDate((f.created_at || '').slice(0, 10))}`}
                  {f.times_read > 1 ? ` · read ${f.times_read}×` : ''}
                  {f.highlights || f.notes ? ` · ${[f.highlights ? `${f.highlights} highlight${f.highlights === 1 ? '' : 's'}` : '', f.notes ? `${f.notes} note${f.notes === 1 ? '' : 's'}` : ''].filter(Boolean).join(', ')}` : ''}
                </span>
                {f.my_rating ? <Hearts value={f.my_rating} /> : null}
              </div>
            </button>
          ))}
          {loaded && !shown.length && (
            <div style={s.empty}>
              {fics.length ? 'nothing here with these filters.' : 'no fics yet — tap “+ add a fic” and upload one from AO3.'}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

const s = {
  page: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', background: '#0e0b0f', color: '#e8ddd0', fontFamily: "'DM Sans', sans-serif", fontWeight: 300 },
  header: { position: 'sticky', top: 0, zIndex: 10, padding: '18px 20px', display: 'flex', alignItems: 'center', gap: '12px', background: 'linear-gradient(180deg, #120f18 0%, #0e0b0f 100%)', borderBottom: '1px solid rgba(201,147,90,0.06)' },
  back: { width: '36px', height: '36px', borderRadius: '50%', border: '1px solid rgba(201,147,90,0.15)', background: 'transparent', color: '#c9935a', fontSize: '16px', cursor: 'pointer', flexShrink: 0 },
  title: { flex: 1, fontFamily: "'Cormorant Garamond', serif", fontSize: '21px', fontStyle: 'italic' },
  addBtn: { padding: '8px 14px', borderRadius: '18px', border: 'none', background: 'linear-gradient(135deg, #8a5f35, #c9935a)', color: '#0e0b0f', fontSize: '12.5px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", fontWeight: 400, flexShrink: 0 },
  addBtnOn: { padding: '8px 14px', borderRadius: '18px', border: '1px solid rgba(201,147,90,0.25)', background: 'transparent', color: '#c9935a', fontSize: '12.5px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", flexShrink: 0 },
  main: { maxWidth: '640px', margin: '0 auto', padding: '18px 20px 90px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: '12px' },
  error: { background: 'rgba(180,60,60,0.12)', border: '1px solid rgba(220,100,100,0.3)', borderRadius: '12px', padding: '10px 14px', fontSize: '12px', color: '#e0a0a0', cursor: 'pointer' },
  notice: { background: 'rgba(201,147,90,0.08)', border: '1px solid rgba(201,147,90,0.22)', borderRadius: '12px', padding: '10px 14px', fontSize: '12px', color: '#c9a57a', cursor: 'pointer', lineHeight: 1.6 },
  addCard: { background: '#1e1825', border: '1px solid rgba(201,147,90,0.14)', borderRadius: '18px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' },
  addTitle: { fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic', fontSize: '19px' },
  hint: { fontSize: '12px', color: '#8a7466', lineHeight: 1.6 },
  row: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
  smallLabel: { fontSize: '10.5px', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#6d5d4e' },
  chip: (on) => ({ padding: '6px 12px', borderRadius: '14px', fontSize: '12px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", border: on ? 'none' : '1px solid rgba(201,147,90,0.18)', background: on ? 'linear-gradient(135deg, #8a5f35, #c9935a)' : 'transparent', color: on ? '#0e0b0f' : '#c9a57a' }),
  primary: { display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '9px 16px', borderRadius: '18px', border: 'none', background: 'linear-gradient(135deg, #8a5f35, #c9935a)', color: '#0e0b0f', fontSize: '12.5px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", fontWeight: 400 },
  primaryOff: { padding: '9px 16px', borderRadius: '18px', border: 'none', background: 'rgba(201,147,90,0.15)', color: 'rgba(201,147,90,0.45)', fontSize: '12.5px', fontFamily: "'DM Sans', sans-serif" },
  quiet: { padding: '8px 10px', border: 'none', background: 'transparent', color: '#8a7466', fontSize: '12px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  handGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '10px', alignItems: 'end' },
  field: { display: 'flex', flexDirection: 'column', gap: '5px' },
  input: { background: '#120f18', border: '1px solid rgba(201,147,90,0.15)', borderRadius: '10px', padding: '9px 11px', color: '#e8ddd0', fontSize: '13px', fontFamily: "'DM Sans', sans-serif", outline: 'none', boxSizing: 'border-box', width: '100%' },
  select: { background: '#120f18', border: '1px solid rgba(201,147,90,0.15)', borderRadius: '10px', padding: '9px 10px', color: '#e8ddd0', fontSize: '12.5px', fontFamily: "'DM Sans', sans-serif", outline: 'none', maxWidth: '100%' },
  summary: { fontSize: '12px', color: '#8a7466' },
  filters: { display: 'flex', gap: '4px', flexWrap: 'wrap' },
  filterBtn: (on) => ({ padding: '6px 11px', borderRadius: '14px', border: 'none', background: on ? 'rgba(201,147,90,0.14)' : 'transparent', color: on ? '#c9935a' : '#6d5d4e', fontSize: '12.5px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }),
  list: { display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '4px' },
  card: { textAlign: 'left', background: '#17131b', border: '1px solid rgba(201,147,90,0.08)', borderRadius: '18px', padding: '14px 16px', cursor: 'pointer', color: 'inherit', fontFamily: 'inherit', fontWeight: 300, display: 'flex', flexDirection: 'column', gap: '6px' },
  cardTop: { display: 'flex', alignItems: 'flex-start', gap: '10px' },
  ficTitle: { fontFamily: "'Cormorant Garamond', serif", fontSize: '19px', lineHeight: 1.25, color: '#e8ddd0' },
  by: { fontSize: '12px', color: '#8a7466', marginTop: '2px' },
  status: (st) => ({ flexShrink: 0, padding: '3px 9px', borderRadius: '11px', fontSize: '11px', background: st === 'dropped' ? 'rgba(200,106,106,0.12)' : 'rgba(201,147,90,0.12)', color: st === 'dropped' ? '#e0a0a0' : st === 'want' ? '#a8927f' : '#c9935a' }),
  pairing: { fontSize: '13px', color: '#c9a57a' },
  fandom: { fontSize: '12px', color: '#8a7466', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  meta: { fontSize: '11.5px', color: '#6d5d4e' },
  metaRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', fontSize: '11.5px', color: '#6d5d4e' },
  empty: { textAlign: 'center', color: '#6d5d4e', fontSize: '13px', lineHeight: 1.8, padding: '30px 12px' },
};
